import { useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, CreditCard, CheckCircle2, Clock, AlertTriangle, XCircle, Sparkles, ShieldCheck, BellRing, Star } from 'lucide-react';
import { api } from '../lib/api';
import { cn } from '../lib/cn';
import { PricingPlans } from '../components/PricingPlans';
import { trackPurchase } from '../lib/analytics';
import { useAuth } from '../auth/AuthProvider';

type SubStatus = 'pending' | 'trialing' | 'active' | 'past_due' | 'canceled';
type Subscription = {
  id: string;
  status: SubStatus;
  billingPeriod: 'monthly' | 'yearly';
  trialEndsAt: string | null;
  currentPeriodEnd: string | null;
  canceledAt: string | null;
  createdAt: string;
  plan: { id: string; name: string; priceMonthly: number; priceYearly: number; currency: string } | null;
};

const STATUS_META: Record<SubStatus, { label: string; cls: string; Icon: typeof Clock }> = {
  pending:  { label: 'Pago pendiente',  cls: 'bg-amber-50 text-amber-700 border-amber-200',     Icon: Clock },
  trialing: { label: 'Prueba gratuita', cls: 'bg-brand-50 text-brand-700 border-brand-200',     Icon: Clock },
  active:   { label: 'Activa',          cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', Icon: CheckCircle2 },
  past_due: { label: 'Pago fallido',    cls: 'bg-red-50 text-red-700 border-red-200',           Icon: AlertTriangle },
  canceled: { label: 'Cancelada',       cls: 'bg-slate-100 text-slate-500 border-slate-200',    Icon: XCircle },
};

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' });
const fmtMoney = (cents: number, currency: string) =>
  (cents / 100).toLocaleString('es-ES', { style: 'currency', currency });

export function Billing() {
  const [searchParams] = useSearchParams();
  const justReturned = searchParams.get('checkout') === 'return';
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['billing', 'subscription'],
    queryFn: () => api<{ subscription: Subscription | null }>('/api/billing/subscription'),
    refetchInterval: (q) => (q.state.data?.subscription?.status === 'pending' ? 4000 : false),
  });
  const sub = data?.subscription ?? null;
  const hasActive = sub && (sub.status === 'trialing' || sub.status === 'active');

  // When the subscription becomes entitled (e.g. just after checkout), refresh
  // the auth user so `user.entitled` flips to true and the paywall gate unlocks
  // the rest of the dashboard without a manual reload.
  const { user, refresh } = useAuth();
  useEffect(() => {
    if (hasActive && user?.entitled !== true) void refresh();
  }, [hasActive, user?.entitled, refresh]);

  // After a successful checkout, once the subscription is active and the user is
  // actually entitled (so the paywall won't bounce them back), send them into
  // the product instead of leaving them on the billing page.
  const navigate = useNavigate();
  useEffect(() => {
    if (justReturned && hasActive && user?.entitled === true) {
      navigate('/dashboard', { replace: true });
    }
  }, [justReturned, hasActive, user?.entitled, navigate]);

  // Fire the "subscription started" conversion (GA purchase + TikTok Subscribe)
  // exactly once per subscription, when the user returns from checkout and the
  // subscription has become trialing/active. Renewals never hit this page, so
  // they won't double-count; the localStorage guard covers refreshes.
  useEffect(() => {
    if (!justReturned || !sub || !sub.plan) return;
    if (sub.status !== 'trialing' && sub.status !== 'active') return;
    const key = `zp_purchase_tracked_${sub.id}`;
    try {
      if (localStorage.getItem(key)) return;
      localStorage.setItem(key, '1');
    } catch { /* private mode — fire anyway */ }
    const cents = sub.billingPeriod === 'yearly' ? sub.plan.priceYearly : sub.plan.priceMonthly;
    trackPurchase({
      value: cents / 100,
      currency: sub.plan.currency,
      plan: sub.plan.name,
      period: sub.billingPeriod,
      eventId: `sub_${sub.id}`, // dedupe with server CAPI Subscribe
    });
  }, [justReturned, sub]);

  const cancel = useMutation({
    mutationFn: () => api('/api/billing/cancel', { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['billing', 'subscription'] }),
  });

  function onCancel() {
    if (window.confirm('¿Seguro que quieres cancelar tu suscripción? Mantendrás el acceso hasta el final del periodo en curso.')) {
      cancel.mutate();
    }
  }

  const firstName = (user?.email ?? '').split('@')[0] ?? '';
  // Brand-new / lapsed users (no active or trialing sub) get a warm activation
  // onboarding instead of the bare "manage subscription" view.
  const needsActivation = !isLoading && !hasActive;

  return (
    <div className="space-y-6">
      {needsActivation ? (
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Sparkles size={22} className="text-brand-600" /> Activa tu prueba gratis de 3 días
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            {firstName ? `¡Bienvenido, ${firstName}! ` : ''}Elige un plan para empezar a usar Zettapost. Hoy no se te cobra nada.
          </p>
        </div>
      ) : (
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><CreditCard size={22} className="text-brand-600" /> Suscripción</h1>
          <p className="mt-1 text-sm text-slate-500">Gestiona tu plan y tu facturación.</p>
        </div>
      )}

      {justReturned && sub?.status === 'pending' && (
        <div className="bg-brand-50 border border-brand-100 rounded-xl px-4 py-3 flex items-center gap-2 text-sm text-brand-700">
          <Loader2 size={15} className="animate-spin" />
          Estamos procesando tu suscripción… esto puede tardar unos segundos.
        </div>
      )}

      {/* current subscription / activation hero */}
      {isLoading ? (
        <div className="flex items-center gap-2 text-slate-400 py-8">
          <Loader2 size={16} className="animate-spin" /> Cargando…
        </div>
      ) : sub && sub.status !== 'canceled' && sub.plan ? (
        <SubscriptionCard sub={sub} onCancel={onCancel} canceling={cancel.isPending} />
      ) : (
        <div className="rounded-2xl border border-brand-100 bg-gradient-to-br from-brand-50 to-white shadow-sm p-5 sm:p-6">
          <p className="text-sm font-semibold text-slate-800 mb-3">Cómo funciona tu prueba</p>
          <div className="grid sm:grid-cols-3 gap-3">
            {[
              { Icon: CreditCard, title: 'Hoy pagas 0 €', desc: 'Eliges plan y empiezas la prueba de 3 días al instante.' },
              { Icon: BellRing, title: 'Te avisamos antes', desc: 'Recibirás un email antes del primer cobro. Sin sorpresas.' },
              { Icon: ShieldCheck, title: 'Cancela cuando quieras', desc: 'En 1 clic desde aquí. Si cancelas antes, no pagas nada.' },
            ].map(({ Icon, title, desc }) => (
              <div key={title} className="flex items-start gap-2.5 rounded-xl bg-white/70 border border-white p-3">
                <span className="h-8 w-8 rounded-lg bg-brand-600/10 text-brand-600 flex items-center justify-center shrink-0">
                  <Icon size={16} />
                </span>
                <div>
                  <p className="text-sm font-semibold text-slate-800 leading-tight">{title}</p>
                  <p className="text-xs text-slate-500 mt-0.5 leading-snug">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* social proof (activation only) */}
      {needsActivation && (
        <div className="flex flex-col sm:flex-row items-center justify-center gap-x-3 gap-y-1.5 text-center">
          <img src="/images-users.webp" alt="" className="h-8 w-auto" loading="lazy" />
          <p className="inline-flex items-center gap-1.5 text-sm text-slate-600">
            <span className="inline-flex text-amber-400">
              {[0, 1, 2, 3, 4].map((i) => <Star key={i} size={14} fill="currentColor" />)}
            </span>
            Marcas y creadores ya programan sus redes con Zettapost
          </p>
        </div>
      )}

      {/* plans */}
      <div className="pt-2">
        <h2 className="text-lg font-bold text-slate-900 mb-1">
          {hasActive ? 'Cambiar de plan' : 'Elige tu plan para empezar'}
        </h2>
        <p className="text-sm text-slate-500 mb-4">
          Todos los planes incluyen 3 días de prueba gratis · cancela cuando quieras.
        </p>
        {needsActivation && (
          <div className="mb-5 flex items-start gap-2.5 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3">
            <ShieldCheck size={18} className="text-emerald-600 shrink-0 mt-0.5" />
            <p className="text-sm text-emerald-900 leading-snug">
              <strong>Garantía de devolución de 14 días.</strong> Si te cobran y no te convence, te
              devolvemos el dinero. Sin preguntas.
            </p>
          </div>
        )}
        <PricingPlans currentPlanId={hasActive ? sub!.plan!.id : null} />
      </div>
    </div>
  );
}

function SubscriptionCard({ sub, onCancel, canceling }: { sub: Subscription; onCancel: () => void; canceling: boolean }) {
  const meta = STATUS_META[sub.status];
  const { Icon } = meta;
  const plan = sub.plan!;
  const price = sub.billingPeriod === 'yearly' ? plan.priceYearly : plan.priceMonthly;
  const cancellable = sub.status === 'trialing' || sub.status === 'active' || sub.status === 'past_due';

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2.5">
            <h2 className="text-lg font-bold text-slate-900">{plan.name}</h2>
            <span className={cn('inline-flex items-center gap-1.5 text-xs font-medium rounded-full border px-2.5 py-1', meta.cls)}>
              <Icon size={12} /> {meta.label}
            </span>
          </div>
          <p className="mt-1 text-sm text-slate-500">
            {fmtMoney(price, plan.currency)} / {sub.billingPeriod === 'yearly' ? 'año' : 'mes'}
          </p>
        </div>
      </div>

      <div className="mt-4 pt-4 border-t border-slate-100 grid sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
        {sub.status === 'trialing' && sub.trialEndsAt && (
          <Row label="Fin de la prueba" value={fmtDate(sub.trialEndsAt)} />
        )}
        {sub.status === 'active' && sub.currentPeriodEnd && (
          <Row label="Próxima renovación" value={fmtDate(sub.currentPeriodEnd)} />
        )}
        {sub.status === 'trialing' && (
          <Row label="Primer cobro" value={sub.trialEndsAt ? fmtDate(sub.trialEndsAt) : '—'} />
        )}
        {sub.status === 'past_due' && (
          <Row label="Estado" value="Revisa tu método de pago" danger />
        )}
        <Row label="Facturación" value={sub.billingPeriod === 'yearly' ? 'Anual' : 'Mensual'} />
      </div>

      {cancellable && (
        <div className="mt-4 pt-4 border-t border-slate-100 flex justify-end">
          <button
            onClick={onCancel}
            disabled={canceling}
            className="btn-secondary gap-2 !text-red-600 hover:!bg-red-50 disabled:opacity-50"
          >
            {canceling && <Loader2 size={14} className="animate-spin" />}
            {canceling ? 'Cancelando…' : 'Cancelar suscripción'}
          </button>
        </div>
      )}
    </div>
  );
}

function Row({ label, value, danger }: { label: string; value: string; danger?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-slate-400">{label}</span>
      <span className={cn('font-medium', danger ? 'text-red-600' : 'text-slate-700')}>{value}</span>
    </div>
  );
}
